import { useState, useEffect, useContext } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { isEqual } from "lodash";
import { CircularProgress } from "@mui/material";

import useMemoryState from "../../../scripts/hooks/useMemoryState";

import { validationSchema } from "./validationSchema";
import { FIELDS } from "./constants";
import Context from "../../../scripts/context/User";
import CustomLink from "../../Link";

export default function PersonalInformation() {
  const { globalState, setGlobalState, getRegistrationStep } =
    useContext(Context);
  const {
    data: values,
    setData: setValues,
    setLoading,
    handleNext,
    activeStep,
    errorsFromAPI,
    setErrorsFromAPI,
  } = useOutletContext();
  const [dataFromAPI, setDataFromAPI] = useState(
    localStorage.getItem("token") || sessionStorage.getItem("token")
      ? values
      : null
  );
  const [countries, setCountries] = useMemoryState("countries", []);
  const [gettingData, setGettingData] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (countries.length > 0 || activeStep !== 0) return;

    axios.get("/getCountries").then(({ data }) => {
      const countries = data.data.map((country) => {
        return {
          label: country,
        };
      });
      setCountries(countries);
    });
    return () => {
      setCountries([]);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (values.email) return;
    if (sessionStorage.getItem("token") || localStorage.getItem("token")) {
      setGettingData(true);
      axios
        .get("register/personal_info/getPersonalInfo")
        .then(({ data }) => {
          setDataFromAPI(data.data);
          setValues(data.data);
          setGettingData(false);
        })
        .catch(() => {
          setGettingData(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues({ ...values, [name]: value });
    if (name === "mobile") { 
      setValues({ ...values, [name]: value.replace(/[^0-9+]/g, "") });
    }
  };

  const textFieldErrors = (field) => errors.find((e) => e.name === field.key);
  const helperTextErrors = (field) =>
    errors.find((e) => e.name === field.key)
      ? errors.find((e) => e.name === field.key).message
      : "";

  const submitForm = (event) => {
    event.preventDefault();
    setErrors([]);
    if (isEqual(values, dataFromAPI)) {
      handleNext();
      setErrorsFromAPI("");

      return;
    };

    validationSchema
      .validate(
        {
          ...values,
          ...(!isEqual(values, dataFromAPI) && {
            update: false,
          }),
        },
        { abortEarly: false }
      )
      .then(() => {
        setLoading(true);

        axios
          .post("register/personal_info/savePersonalInfo", values)
          .then(({ data }) => {
            if (
              localStorage.getItem("token") ||
              sessionStorage.getItem("token")
            ) {
              setLoading(false);
              setErrorsFromAPI("");
              handleNext();
              setGlobalState({
                ...globalState,
                registration_step:
                  globalState.registration_step > 0
                    ? globalState.registration_step
                    : 1,
              });
            } else {
              localStorage.setItem("token", data.token);
              axios
                .get("/getUserData", {
                  headers: {
                    Authorization: `Bearer ${data.token}`,
                  },
                })
                .then(({ data }) => {
                  setGlobalState({
                    ...globalState,
                    user: {
                      ...data.user,
                    },
                    redirect_uri: data.redirect_uri,
                    registration_step: getRegistrationStep(data.redirect_uri),
                  });
                  setLoading(false);
                  setErrorsFromAPI("");
                  handleNext();
                });
            }
          })
          .catch((error) => {
            setLoading(false);
            const err = error.response.data.errors;
            Object.keys(err).forEach((key) => {
              setErrors([
                {
                  name: key,
                  message: err[key][0],
                },
              ]);
            });
          });
      })
      .catch((err) => {
        setLoading(false);
        const errors = err.inner.map((e) => {
          return {
            name: e.path,
            message: e.message,
          };
        });
        setErrors(errors);
      });
  };

  if (gettingData)
    return (
      <div
        style={{
          height: "30vh",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </div>
    );

  return (
    <form
      className="form"
      novalidate="novalidate"
      id="PersonalInformation"
      onSubmit={submitForm}
    >
      <div
        className="pb-5"
        data-wizard-type="step-content"
        data-wizard-state="current"
      >
        <div className="pb-10 pb-lg-10">
          <h3 className="font-weight-bolder text-white display5">
            Create Account
          </h3>
          {!localStorage.getItem("token") && !sessionStorage.getItem("token") && (
            <div className="text-muted font-weight-bold font-size-h4">
              Already have an Account?{" "}
              <CustomLink
                to="/login"
                className="text-primary font-weight-bolder"
              >
                Sign In
              </CustomLink>
            </div>
          )}
        </div>
        {errorsFromAPI && (
          <div className="d-flex align-items-center bg-light-danger rounded p-5 mb-9 status-div">
            <span className="svg-icon svg-icon-danger mr-5">
              <span className="svg-icon svg-icon-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24px"
                  height="24px"
                  viewBox="0 0 24 24"
                  version="1.1"
                >
                  <g
                    stroke="none"
                    stroke-width="1"
                    fill="none"
                    fill-rule="evenodd"
                  >
                    <rect x="0" y="0" width="24" height="24"></rect>
                    <path
                      d="M11.1669899,4.49941818 L2.82535718,19.5143571 C2.557144,19.9971408 2.7310878,20.6059441 3.21387153,20.8741573 C3.36242953,20.9566895 3.52957021,21 3.69951446,21 L21.2169432,21 C21.7692279,21 22.2169432,20.5522847 22.2169432,20 C22.2169432,19.8159952 22.1661743,19.6355579 22.070225,19.47855 L12.894429,4.4636111 C12.6064401,3.99235656 11.9909517,3.84379039 11.5196972,4.13177928 C11.3723594,4.22181902 11.2508468,4.34847583 11.1669899,4.49941818 Z"
                      fill="#000000"
                      opacity="0.3"
                    ></path>
                    <rect
                      fill="#000000"
                      x="11"
                      y="9"
                      width="2"
                      height="7"
                      rx="1"
                    ></rect>
                    <rect
                      fill="#000000"
                      x="11"
                      y="17"
                      width="2"
                      height="2"
                      rx="1"
                    ></rect>
                  </g>
                </svg>
              </span>
            </span>
            <div className="d-flex flex-column flex-grow-1 mr-2">
              <span
                href="#"
                className="text-danger py-1 font-size-lg font-size-status"
              >
                Rejected
              </span>
              <p className="text-white fw-bold d-block text-break">
                {errorsFromAPI
                  .replace(/(\r\n|\n|\r)/gm, " ")
                  .split(" ")
                  .map((word, index) => (
                    <span key={index}>
                      {word}
                      <br />
                    </span>
                  ))}
              </p>
            </div>
          </div>
        )}
        <div className="row">
          {FIELDS.filter((field) =>
            dataFromAPI
              ? !["password", "password_confirmation"].includes(field.key)
              : true
          ).map((field, index) =>
            field.key === "country" ? (
              <div key={index} className="form-group col-md-6">
                <label className="font-size-h6 font-weight-bolder text-white">
                  Country
                </label>
                <select
                  value={values[field.key]}
                  onChange={handleChange}
                  name="country"
                  className={`round form-control h-auto text-white placeholder-white bg-dark-o-70 rounded-pill border-1 py-4 px-8${
                    textFieldErrors(field) ? " is-invalid" : ""
                  }`}
                >
                  <option value="">Select</option>
                  {countries.map((country, index) => (
                    <option key={index} value={country.label}>
                      {country.label}
                    </option>
                  ))}
                </select>
                {textFieldErrors(field) && (
                  <div className="fv-plugins-message-container">
                    <div
                      data-field={field.key}
                      data-validator={helperTextErrors(field)}
                      className="fv-help-block"
                    >
                      {helperTextErrors(field)}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div key={index} className="form-group col-md-6">
                <label
                  htmlFor={field.key}
                  className="font-size-h6 font-weight-bolder text-white"
                >
                  {field.label}
                </label>
                <input
                  id={field.key}
                  type={field.type}
                  className={`form-control h-auto text-white placeholder-white bg-dark-o-70 rounded-pill border-1 py-4 px-8${
                    textFieldErrors(field) ? " is-invalid" : ""
                  }`}
                  name={field.key}
                  placeholder={field.placeholder}
                  disabled={
                    dataFromAPI &&
                    ["password", "password_confirmation", "email"].includes(
                      field.key
                    )
                  }
                  value={values[field.key]}
                  onChange={handleChange}
                  maxLength={field.max}
                />
                {textFieldErrors(field) && (
                  <div className="fv-plugins-message-container">
                    <div
                      data-field={field.key}
                      data-validator={helperTextErrors(field)}
                      className="fv-help-block"
                    >
                      {helperTextErrors(field)}
                    </div>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>
    </form>
  );
}
